import type { User } from 'firebase/auth';
import { computed, ref, type Ref } from 'vue';
import { useCurrentUser } from 'vuefire';

// Global debug state
const debugUserId = ref<string | null>(null);
const isDebugMode = ref(false);

// Initialize userId from localStorage if it exists
if (localStorage.getItem('debugUserId')) {
  debugUserId.value = localStorage.getItem('debugUserId');
  isDebugMode.value = true;
}

export function useDebugUser(): Ref<User | null> {
  const actualUser = useCurrentUser();

  const computedUser = computed(() => {
    if (import.meta.env.DEV && isDebugMode.value && debugUserId.value && actualUser.value) {
      return {
        ...actualUser.value,
        uid: debugUserId.value,
      };
    }
    return actualUser.value || null;
  });

  return computedUser;
}

export function useConfigureDebugUser() {
  const actualUser = useCurrentUser();
  // Return either the debug user ID or the actual user ID
  const currentUserId = computed(() => {
    if (import.meta.env.DEV && isDebugMode.value && debugUserId.value) {
      return debugUserId.value;
    }
    return actualUser.value?.uid || null;
  });

  const setDebugUserId = (userId: string | null) => {
    if (import.meta.env.DEV) {
      debugUserId.value = userId;
      isDebugMode.value = !!userId;
      if (userId) {
        localStorage.setItem('debugUserId', userId);
      }
      else {
        localStorage.removeItem('debugUserId');
      }
    }
  };

  const clearDebugUser = () => {
    if (import.meta.env.DEV) {
      debugUserId.value = null;
      isDebugMode.value = false;
      localStorage.removeItem('debugUserId');
    }
  };

  return {
    currentUserId,
    isDebugMode: computed(() => import.meta.env.DEV && isDebugMode.value),
    setDebugUserId,
    clearDebugUser,
    actualUser,
  };
}
